<script setup>
import { computed } from 'vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisVersionDiffPanel — side-by-side / row diff for versioned entities
 * (listing, mandato, automação, contrato, template, contraproposta). The
 * domain passes already-formatted before/after values (money/unit formatting is
 * the caller's job) and this panel highlights what changed and exposes an
 * optional rollback affordance. Business rules (who can rollback) stay outside.
 *
 * Field: { key?, label, before, after, unit?, redacted? }
 */
const props = defineProps({
  fields: { type: Array, default: () => [] },
  beforeLabel: { type: String, default: 'Versão atual' },
  afterLabel: { type: String, default: 'Nova versão' },
  actor: { type: String, default: '' },
  at: { type: String, default: '' },
  layout: {
    type: String,
    default: 'side-by-side',
    validator: value => ['side-by-side', 'rows'].includes(value),
  },
  canRollback: { type: Boolean, default: false },
  rollbackLabel: { type: String, default: 'Reverter' },
  changedLabel: { type: String, default: 'Alterado' },
  onlyChanged: { type: Boolean, default: false },
  mask: { type: String, default: '••••••' },
  emptyValue: { type: String, default: '—' },
  emptyLabel: { type: String, default: 'Sem alterações para comparar' },
  ariaLabel: { type: String, default: 'Comparação de versões' },
});

const emit = defineEmits(['rollback']);

const normalize = value =>
  value === null || value === undefined || value === '' ? '' : String(value);

const isChanged = field => normalize(field.before) !== normalize(field.after);

const displayValue = (field, side) => {
  if (field.redacted) return props.mask;
  const raw = normalize(field[side]);
  if (raw === '') return props.emptyValue;
  return field.unit ? `${raw} ${field.unit}` : raw;
};

const rows = computed(() =>
  props.fields
    .filter(field => field && field.label)
    .filter(field => (props.onlyChanged ? isChanged(field) : true))
    .map((field, index) => ({
      ...field,
      key: field.key ?? `${field.label}-${index}`,
      changed: isChanged(field),
    }))
);
const changedCount = computed(() => rows.value.filter(r => r.changed).length);
const headerLabel = computed(
  () => `${props.beforeLabel} → ${props.afterLabel}`
);
const metaLine = computed(() =>
  [props.actor, props.at].filter(Boolean).join(' · ')
);
</script>

<template>
  <section
    :aria-label="ariaLabel"
    class="flex flex-col gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card"
  >
    <header class="flex flex-wrap items-center justify-between gap-2">
      <div class="min-w-0">
        <p class="text-lais-label-xs uppercase text-lais-text-muted">
          {{ headerLabel }}
        </p>
        <p v-if="metaLine" class="text-lais-body-xs text-lais-text-muted">
          {{ metaLine }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <LaisStatusBadge
          :label="`${changedCount}`"
          :tone="changedCount > 0 ? 'warning' : 'neutral'"
          :icon="changedCount > 0 ? 'i-lucide-git-compare' : 'i-lucide-equal'"
          size="sm"
        />
        <button
          v-if="canRollback"
          type="button"
          class="inline-flex h-8 items-center gap-1.5 rounded-lais border border-lais-border bg-lais-bg-card px-3 text-lais-label-sm font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
          @click="emit('rollback')"
        >
          <span class="i-lucide-rotate-ccw size-3.5" aria-hidden="true" />
          {{ rollbackLabel }}
        </button>
      </div>
    </header>

    <p
      v-if="!rows.length"
      class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-lais-body-sm text-lais-text-muted"
    >
      {{ emptyLabel }}
    </p>

    <ul v-else role="list" class="grid gap-2">
      <li
        v-for="row in rows"
        :key="row.key"
        class="rounded-lais-sm border px-3 py-2.5"
        :class="
          row.changed
            ? 'border-lais-warning/40 bg-lais-warning/10'
            : 'border-lais-border-soft bg-lais-bg-soft'
        "
      >
        <div class="flex items-center justify-between gap-2">
          <p
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ row.label }}
          </p>
          <LaisStatusBadge
            v-if="row.changed"
            :label="changedLabel"
            tone="warning"
            size="sm"
          />
        </div>
        <div
          class="mt-1.5 grid gap-1"
          :class="layout === 'side-by-side' ? 'sm:grid-cols-2 sm:gap-3' : ''"
        >
          <p
            class="min-w-0 break-words text-lais-body-sm"
            :class="
              row.changed
                ? 'text-lais-text-muted line-through'
                : 'text-lais-text'
            "
          >
            {{ displayValue(row, 'before') }}
          </p>
          <p
            class="min-w-0 break-words text-lais-body-sm font-semibold"
            :class="row.changed ? 'text-lais-text' : 'text-lais-text-muted'"
          >
            {{ displayValue(row, 'after') }}
          </p>
        </div>
      </li>
    </ul>
  </section>
</template>
